// ============================================================================
// PÁGINA: SOLICITAR DIPLOMA - VERSÃO CORRIGIDA
// ============================================================================
// Arquivo: src/pages/servicos/SolicitarDiploma.tsx
// Alterações: Ícones trocados por imagens + Correção Step 3
// ============================================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface FormData {
  // Dados Pessoais
  name: string;
  cpf: string;
  rg: string;
  email: string;
  phone: string;
  
  // Dados do Curso
  courseId: string;
  courseName: string;
  studentId: string;
  
  // Endereço de Entrega
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  
  // Tipo de Solicitação
  requestType: 'first' | 'second';
  
  // Observações
  observations: string;
}

interface ShippingOption {
  id: string;
  name: string;
  price: number;
  days: string;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const SolicitarDiploma: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    cpf: '',
    rg: '',
    email: '',
    phone: '',
    courseId: '',
    courseName: '',
    studentId: '',
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    requestType: 'first',
    observations: ''
  });
  
  const [selectedShipping, setSelectedShipping] = useState<string>('');
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const shippingOptions: ShippingOption[] = [
    {
      id: 'sedex',
      name: 'SEDEX',
      price: 28.50,
      days: '2-4 dias úteis'
    },
    {
      id: 'pac',
      name: 'PAC',
      price: 18.90,
      days: '7-10 dias úteis'
    }
  ];

  // ========================================================================
  // FUNÇÕES AUXILIARES
  // ========================================================================

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const searchCep = async () => {
    if (formData.cep.replace(/\D/g, '').length !== 8) return;
    
    setIsLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${formData.cep.replace(/\D/g, '')}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          street: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setIsLoadingCep(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep < 4) setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    console.log('Enviando solicitação:', formData);
    setShowSuccess(true);
  };

  const calculateTotal = () => {
    const diplomaPrice = formData.requestType === 'first' ? 120.00 : 80.00;
    const shipping = shippingOptions.find(opt => opt.id === selectedShipping);
    const shippingPrice = shipping ? shipping.price : 0;
    return diplomaPrice + shippingPrice;
  };

  // ========================================================================
  // STEPS DO FORMULÁRIO
  // ========================================================================

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="absolute inset-0 bg-amber-400/20 rounded-2xl blur-xl"></div>
          <img
            src="/icons/icone2.png"
            alt="Dados Pessoais"
            className="relative z-10 w-20 h-20 object-contain drop-shadow-lg"
          />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Dados Pessoais</h3>
        <p className="text-gray-600">Informe seus dados cadastrados na Federação</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Nome Completo *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="Seu nome completo"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            ID do Aluno *
          </label>
          <input
            type="text"
            value={formData.studentId}
            onChange={(e) => handleInputChange('studentId', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="Ex: ALU-2024-00123"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            CPF *
          </label>
          <input
            type="text"
            value={formData.cpf}
            onChange={(e) => handleInputChange('cpf', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="000.000.000-00"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            RG *
          </label>
          <input
            type="text"
            value={formData.rg}
            onChange={(e) => handleInputChange('rg', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="00.000.000-0"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            E-mail *
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="seu@email.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Telefone *
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="(00) 00000-0000"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="absolute inset-0 bg-blue-400/20 rounded-2xl blur-xl"></div>
          <img
            src="/icons/icon9.png"
            alt="Dados do Curso"
            className="relative z-10 w-20 h-20 object-contain drop-shadow-lg"
          />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Dados do Curso</h3>
        <p className="text-gray-600">Selecione o curso para emissão do diploma</p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Tipo de Solicitação *
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => handleInputChange('requestType', 'first')}
              className={`p-6 border-2 rounded-xl transition-all ${
                formData.requestType === 'first'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-300 hover:border-amber-300'
              }`}
            >
              <div className="relative w-12 h-12 mx-auto mb-3">
                <div className="absolute inset-0 bg-amber-400/20 rounded-lg blur-lg"></div>
                <img
                  src="/icons/icon13.png"
                  alt="1ª Via"
                  className="relative z-10 w-12 h-12 object-contain"
                />
              </div>
              <p className="font-bold text-gray-900">1ª Via</p>
              <p className="text-sm text-gray-600">R$ 120,00</p>
            </button>
            
            <button
              type="button"
              onClick={() => handleInputChange('requestType', 'second')}
              className={`p-6 border-2 rounded-xl transition-all ${
                formData.requestType === 'second'
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-300 hover:border-amber-300'
              }`}
            >
              <div className="relative w-12 h-12 mx-auto mb-3">
                <div className="absolute inset-0 bg-amber-400/20 rounded-lg blur-lg"></div>
                <img
                  src="/icons/icon13.png"
                  alt="2ª Via"
                  className="relative z-10 w-12 h-12 object-contain"
                />
              </div>
              <p className="font-bold text-gray-900">2ª Via</p>
              <p className="text-sm text-gray-600">R$ 80,00</p>
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Nome do Curso *
          </label>
          <select
            value={formData.courseName}
            onChange={(e) => handleInputChange('courseName', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            required
          >
            <option value="">Selecione o curso</option>
            <option value="Cabeleireiro Profissional">Cabeleireiro Profissional</option>
            <option value="Manicure e Pedicure">Manicure e Pedicure</option>
            <option value="Maquiagem Profissional">Maquiagem Profissional</option>
            <option value="Designer de Sobrancelhas">Designer de Sobrancelhas</option>
            <option value="Estética Facial">Estética Facial</option>
            <option value="Barbeiro Profissional">Barbeiro Profissional</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Observações (Opcional)
          </label>
          <textarea
            value={formData.observations}
            onChange={(e) => handleInputChange('observations', e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            placeholder="Alguma informação adicional sobre sua solicitação..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="absolute inset-0 bg-green-400/20 rounded-2xl blur-xl"></div>
          <img
            src="/icons/icon14.png"
            alt="Endereço"
            className="relative z-10 w-20 h-20 object-contain drop-shadow-lg"
          />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Endereço de Entrega</h3>
        <p className="text-gray-600">Informe onde deseja receber seu diploma</p>
      </div>

      <div className="space-y-6">
        {/* LINHA 1: CEP */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              CEP *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.cep}
                onChange={(e) => handleInputChange('cep', e.target.value)}
                onBlur={searchCep}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                placeholder="00000-000"
                required
              />
              
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Rua/Avenida *
            </label>
            <input
              type="text"
              value={formData.street}
              onChange={(e) => handleInputChange('street', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              placeholder="Nome da rua"
              required
            />
          </div>
        </div>

        {/* LINHA 2: NÚMERO E COMPLEMENTO */}
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Número *
            </label>
            <input
              type="text"
              value={formData.number}
              onChange={(e) => handleInputChange('number', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              placeholder="Nº"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Complemento
            </label>
            <input
              type="text"
              value={formData.complement}
              onChange={(e) => handleInputChange('complement', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              placeholder="Apto, bloco, etc."
            />
          </div>
        </div>

        {/* LINHA 3: BAIRRO, CIDADE, ESTADO */}
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Bairro *
            </label>
            <input
              type="text"
              value={formData.neighborhood}
              onChange={(e) => handleInputChange('neighborhood', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              placeholder="Bairro"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Cidade *
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              placeholder="Cidade"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Estado *
            </label>
            <select
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              required
            >
              <option value="">UF</option>
              <option value="AC">AC</option>
              <option value="AL">AL</option>
              <option value="AP">AP</option>
              <option value="AM">AM</option>
              <option value="BA">BA</option>
              <option value="CE">CE</option>
              <option value="DF">DF</option>
              <option value="ES">ES</option>
              <option value="GO">GO</option>
              <option value="MA">MA</option>
              <option value="MT">MT</option>
              <option value="MS">MS</option>
              <option value="MG">MG</option>
              <option value="PA">PA</option>
              <option value="PB">PB</option>
              <option value="PR">PR</option>
              <option value="PE">PE</option>
              <option value="PI">PI</option>
              <option value="RJ">RJ</option>
              <option value="RN">RN</option>
              <option value="RS">RS</option>
              <option value="RO">RO</option>
              <option value="RR">RR</option>
              <option value="SC">SC</option>
              <option value="SP">SP</option>
              <option value="SE">SE</option>
              <option value="TO">TO</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="relative w-20 h-20 mx-auto mb-4">
          <div className="absolute inset-0 bg-purple-400/20 rounded-2xl blur-xl"></div>
          <img
            src="/icons/icon15.png"
            alt="Pagamento"
            className="relative z-10 w-20 h-20 object-contain drop-shadow-lg"
          />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Frete e Pagamento</h3>
        <p className="text-gray-600">Escolha a forma de entrega e pagamento</p>
      </div>

      {/* Resumo do Pedido */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <h4 className="font-bold text-gray-900 mb-4">Resumo do Pedido</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Diploma ({formData.requestType === 'first' ? '1ª Via' : '2ª Via'})</span>
            <span className="font-semibold">R$ {formData.requestType === 'first' ? '120,00' : '80,00'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Frete</span>
            <span className="font-semibold">
              {selectedShipping ? `R$ ${shippingOptions.find(opt => opt.id === selectedShipping)?.price.toFixed(2)}` : '-'}
            </span>
          </div>
          <div className="border-t border-gray-300 pt-3 flex justify-between">
            <span className="font-bold text-gray-900">Total</span>
            <span className="font-bold text-amber-600 text-xl">R$ {calculateTotal().toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Opções de Frete */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          Escolha o Frete *
        </label>
        <div className="grid gap-4">
          {shippingOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelectedShipping(option.id)}
              className={`p-4 border-2 rounded-xl flex items-center justify-between transition-all ${
                selectedShipping === option.id
                  ? 'border-amber-500 bg-amber-50'
                  : 'border-gray-300 hover:border-amber-300'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="relative w-12 h-12">
                  <div className="absolute inset-0 bg-amber-400/15 rounded-lg blur-lg"></div>
                  <img
                    src={option.id === 'sedex' ? '/icons/icon14.png' : '/icons/icon14.png'}
                    alt={option.name}
                    className="relative z-10 w-12 h-12 object-contain"
                  />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900">{option.name}</p>
                  <p className="text-sm text-gray-600">{option.days}</p>
                </div>
              </div>
              <p className="font-bold text-amber-600">R$ {option.price.toFixed(2)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Formas de Pagamento */}
      <div className="bg-blue-50 rounded-xl p-6">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <img src="/icons/icon15.png" alt="Pagamento" className="w-5 h-5" />
          Formas de Pagamento
        </h4>
        <div className="space-y-2 text-sm text-gray-700">
          <p>✅ PIX (Aprovação imediata)</p>
          <p>✅ Cartão de Crédito (até 3x sem juros)</p>
          <p>✅ Boleto Bancário (aprovação em 1-2 dias úteis)</p>
          <p>✅ Transferência / Depósito</p>
        </div>
      </div>

      {/* Informações Importantes */}
      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          Informações Importantes
        </h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>• Prazo de confecção: <strong>7 a 10 dias úteis</strong> após confirmação do pagamento</li>
          <li>• O diploma será enviado para o endereço informado</li>
          <li>• Você receberá um código de rastreamento por e-mail</li>
          <li>• O pagamento deve ser efetuado imediatamente para liberação</li>
        </ul>
      </div>
    </div>
  );

  // ========================================================================
  // MODAL DE SUCESSO
  // ========================================================================

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30 py-20">
        <div className="container mx-auto px-6 max-w-2xl">
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-black text-gray-900 mb-4">
              Solicitação Enviada com Sucesso!
            </h2>
            
            <p className="text-lg text-gray-600 mb-8">
              Recebemos sua solicitação de diploma. Em breve você receberá um e-mail com o link de pagamento e instruções para acompanhamento.
            </p>

            <div className="bg-amber-50 rounded-xl p-6 mb-8 text-left">
              <h4 className="font-bold text-gray-900 mb-3">Próximos Passos:</h4>
              <ol className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">1.</span>
                  <span>Verifique seu e-mail com o link de pagamento</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">2.</span>
                  <span>Realize o pagamento através do método escolhido</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">3.</span>
                  <span>Aguarde a confirmação e início da confecção (7-10 dias úteis)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-amber-600">4.</span>
                  <span>Receba o código de rastreamento por e-mail</span>
                </li>
              </ol>
            </div>

            <div className="flex gap-4 justify-center">
              <Link
                to="/"
                className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
              >
                Voltar ao Início
              </Link>
              <Link
                to="/login/aluno"
                className="px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-lg hover:shadow-lg transition-all"
              >
                Acessar Meu Portal
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER PRINCIPAL
  // ========================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">
      
      {/* HERO SECTION */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-gray-900 via-amber-900 to-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/30 mb-6">
              <img src="/icons/icon7.png" alt="Diploma" className="w-6 h-6 object-contain" />
              <span className="text-sm font-bold text-amber-100 uppercase tracking-wider">
                Serviços
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
              Solicitar Diploma
            </h1>
            <p className="text-lg text-amber-100">
              Solicite seu diploma ou 2ª via de forma rápida e segura
            </p>
          </div>
        </div>
      </section>

      {/* PROGRESS STEPPER */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                        currentStep >= step
                          ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step ? <CheckCircle className="w-6 h-6" /> : step}
                    </div>
                    <p className={`text-xs mt-2 font-semibold ${currentStep >= step ? 'text-amber-600' : 'text-gray-400'}`}>
                      {step === 1 && 'Dados'}
                      {step === 2 && 'Curso'}
                      {step === 3 && 'Endereço'}
                      {step === 4 && 'Pagamento'}
                    </p>
                  </div>
                  {step < 4 && (
                    <div className={`flex-1 h-1 ${currentStep > step ? 'bg-amber-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FORMULÁRIO */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              
              {/* STEPS CONTENT */}
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}

              {/* NAVIGATION BUTTONS */}
              <div className="flex gap-4 mt-10 pt-8 border-t border-gray-200">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="flex-1 px-6 py-4 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Voltar
                  </button>
                )}
                
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2"
                  >
                    Próximo
                    <ArrowRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!selectedShipping}
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold rounded-xl hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Finalizar Solicitação
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default SolicitarDiploma;
